import {
  IAssignment,
  IAssignmentAttachment,
  IAssignmentRoleProposal,
  IProposalAttachment,
} from 'types/assignmentInterfaces'
import { IOrganization } from 'types/userInterfaces'
import { NewOrExisting } from 'Components/reusable/DataContext'

const DRAFT_TTL_MS = 24 * 60 * 60 * 1000

type DraftEnvelope<T> = {
  version: number
  updatedAt: number
  expiresAt: number
  payload: T
}

export type AssignmentCreateDraft = {
  data: NewOrExisting<IAssignment>
}

export type ProposeRoleDraft = {
  context: {
    roleId: number
    assignmentId: number
  }
  proposal: NewOrExisting<IAssignmentRoleProposal>
  sendEmailCopy: boolean
  selectedSalesIds: number[]
  supplierId: number | null
}

const sanitizeAttachments = <T extends { Upload?: { id?: number } }>(attachments: T[] | undefined): T[] => {
  if (!Array.isArray(attachments)) {
    return []
  }

  return attachments.filter(item => !!item?.Upload?.id)
}

export const sanitizeAssignmentDraftData = (data: NewOrExisting<IAssignment>): NewOrExisting<IAssignment> => {
  return {
    ...data,
    attachments: sanitizeAttachments(data.attachments as IAssignmentAttachment[]),
  } as NewOrExisting<IAssignment>
}

export const sanitizeProposalDraftData = (
  data: NewOrExisting<IAssignmentRoleProposal>
): NewOrExisting<IAssignmentRoleProposal> => {
  return {
    ...data,
    attachments: sanitizeAttachments(data.attachments as IProposalAttachment[]),
  } as NewOrExisting<IAssignmentRoleProposal>
}

const saveDraft = <T>(storage: Storage, key: string, payload: T, ttlMs: number): void => {
  const envelope: DraftEnvelope<T> = {
    version: 1,
    updatedAt: Date.now(),
    expiresAt: Date.now() + ttlMs,
    payload,
  }

  storage.setItem(key, JSON.stringify(envelope))
}

const loadDraft = <T>(storage: Storage, key: string): T | null => {
  const raw = storage.getItem(key)
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as DraftEnvelope<T>
    if (!parsed?.payload || !parsed?.expiresAt) {
      storage.removeItem(key)
      return null
    }

    if (Date.now() > parsed.expiresAt) {
      storage.removeItem(key)
      return null
    }

    return parsed.payload
  } catch {
    storage.removeItem(key)
    return null
  }
}

export const saveSessionDraft = <T>(key: string, payload: T, ttlMs = DRAFT_TTL_MS): void => {
  if (typeof window === 'undefined') return
  saveDraft(window.sessionStorage, key, payload, ttlMs)
}

export const loadSessionDraft = <T>(key: string): T | null => {
  if (typeof window === 'undefined') return null
  return loadDraft<T>(window.sessionStorage, key)
}

export const clearSessionDraft = (key: string): void => {
  if (typeof window === 'undefined') return
  window.sessionStorage.removeItem(key)
}

/**
 * localStorage variants survive closing the browser. They are still wiped by
 * `removeNonCaleoStorage()` on logout and session expiry. Storage errors (blocked
 * storage, quota) are swallowed: a lost draft must never break the page.
 */
export const saveLocalDraft = <T>(key: string, payload: T, ttlMs = DRAFT_TTL_MS): void => {
  try {
    saveDraft(window.localStorage, key, payload, ttlMs)
  } catch {
    // ignore
  }
}

export const loadLocalDraft = <T>(key: string): T | null => {
  try {
    return loadDraft<T>(window.localStorage, key)
  } catch {
    return null
  }
}

const getLocalKeys = (keyPrefix: string): string[] => {
  const keys: string[] = []
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i)
    if (key?.startsWith(keyPrefix)) keys.push(key)
  }
  return keys
}

export const clearLocalDraft = (key: string): void => {
  try {
    window.localStorage.removeItem(key)
  } catch {
    // ignore
  }
}

export const clearLocalDrafts = (keyPrefix: string): void => {
  try {
    for (const key of getLocalKeys(keyPrefix)) {
      window.localStorage.removeItem(key)
    }
  } catch {
    // ignore
  }
}

/** Keys of unexpired drafts starting with the prefix. Expired ones are removed. */
export const listLocalDraftKeys = (keyPrefix: string): string[] => {
  try {
    return getLocalKeys(keyPrefix).filter(key => loadLocalDraft(key) !== null)
  } catch {
    return []
  }
}

export const clearAllSessionDrafts = (): void => {
  if (typeof window === 'undefined') return
  const keys = Object.keys(window.sessionStorage).filter(k => k.startsWith('draft:'))
  for (const key of keys) {
    window.sessionStorage.removeItem(key)
  }
}

export const buildAssignmentCreateDraftKey = (userId?: number, organizationId?: number): string => {
  return `draft:assignment-create:${userId ?? 'unknown'}:${organizationId ?? 'unknown'}`
}

export const buildProposeRoleDraftKey = (
  userId: number | undefined,
  organization: IOrganization | undefined,
  assignmentId: number,
  roleId: number
): string => {
  return `draft:propose-role:${userId ?? 'unknown'}:${organization?.id ?? 'unknown'}:${assignmentId}:${roleId}`
}
